import { ChangeDetectorRef, Component, EventEmitter, Input, Output, QueryList, ViewChildren, ViewContainerRef, ViewRef } from '@angular/core';

import { ECSNode } from '../../../classes/tree/nodes/ECSNode';
import { Tree } from '../../../classes/tree/tree';
import { InputConverter, JSONConverter } from '../../../inputConverter';
import { DocumentService } from '../../../services/document.service';
import { IDocumentSource, MapCoreDocument } from '../../../types';
import { BaseComponent } from '../../base.component';
import { DocSourceItemComponent } from '../doc-source-item/doc-source-item.component';

@Component({
    selector: 'mc-doc-source-ng',
    templateUrl: './doc-source.component.html',
    styleUrls: ['./doc-source.component.scss']
})
export class DocSourceComponent extends BaseComponent {

    private _currentFeature: { [key: string]: any } = null;
    get currentFeature() {
        return this._currentFeature;
    }
    @Input()
    @InputConverter(JSONConverter)
    set currentFeature(value) {
        this._currentFeature = JSONConverter(value);
        this.updateDocSources();
    }

    private _currentTable: { [key: string]: any } = null;
    get currentTable() {
        return this._currentTable;
    }
    @Input()
    @InputConverter(JSONConverter)
    set currentTable(value) {
        this._currentTable = JSONConverter(value);
        this.updateDocSources();
    }

    public _docSources: IDocumentSource[];
    get docSources(): IDocumentSource[] {
        return this._docSources;
    }
    set docSources(value) {
        this._docSources = value;
        this.loading = false;
        if (this.cdr && !(this.cdr as ViewRef).destroyed) {
            this.cdr.detectChanges();
        }
    }
    public loading: boolean = true;
    public currentRecord: any;

    @Output() public readonly docSourcesUpdated = new EventEmitter<IDocumentSource[]>();

    currentIndex: number = null;
    docSourceItems: any[];
    documents: MapCoreDocument[] = [];
    imgVideoDocs: MapCoreDocument[] = [];

    @ViewChildren(DocSourceItemComponent)
    items: QueryList<DocSourceItemComponent>;

    constructor(_vcr: ViewContainerRef, private readonly documentService: DocumentService, private readonly cdr: ChangeDetectorRef) {
        super(_vcr);
        this.registerEvent('docSourcesUpdated', this.docSourcesUpdated);
    }

    buildDocumentGroups(tree: Tree<ECSNode>) {
        const groups = {};
        for (const node of tree.children) {
            const key = node.data.record.documentType;

            if (!groups[key]) {
                groups[key] = [];
            }

            groups[key].push(node);
        }
        return Object.entries(groups);
    }

    async updateDocSources() {
        this.loading = true;
        this.clearDocSources();
        let sources;
        let attributes;
        let layer;
        if (this.currentFeature) {
            if (this.currentFeature.sourceLayer && this.currentFeature.sourceLayer.source && this.currentFeature.sourceLayer.source.dataSource) {
                const layerId = this.currentFeature.sourceLayer.source.dataSource.workspaceId;
                sources = layerId ? await this.documentService.getLayerDocSources(layerId) : null;
            }
            attributes = this.currentFeature.attributes;
            layer = this.currentFeature.layer;
        } else if (this.currentTable && this.currentTable.tableId) {
            sources = await this.documentService.getTableDocSources(this.currentTable.tableId);
            attributes = this.currentTable.attributes;
        }
        const docSources = sources && sources.length ? sources[0].docSources : [];
        this.documents = await this.documentService.getDocuments({ attributes }, docSources, layer);
        this.imgVideoDocs = this.documents.filter(d => this.documentService.isImage(d) || this.documentService.isVideo(d));
        if (this.documents.length) {
            this.documents.forEach((document, index) => {
                const columnName = `DOCUMENT_URL_${index.toString()}`;

                // If we revisit a previously visited layer in the identify dialog, the document sources will be
                // unloaded, but the previously added attributes will remain. Don't add them again.
                if (!attributes.some(a => a.columnName === columnName)) {
                    attributes.push({
                        columnName,
                        displayName: columnName,
                        value: document.source === 'ECS' ? `${document.url}&download=1` : document.url,
                        displayText: document.displayText,
                        type: document.source === 'ECS' ? 'download-url' : 'open-url'
                    });
                }
            });
            this.currentIndex = 0;
        }
        this.currentRecord = { attributes };
        if (docSources) {
            this.docSources = docSources;
            this.docSourcesUpdated.emit(this.docSources);
        }
        return;
    }

    get currentDocument(): MapCoreDocument {
        if (this.currentIndex !== null && this.imgVideoDocs.length) {
            return this.imgVideoDocs[this.currentIndex];
        }
    }

    moveForward() {
        this.currentIndex++;
    }

    moveBackward() {
        this.currentIndex--;
    }

    clearDocSources() {
        this.docSources = [];
        this.documents = [];
        this.imgVideoDocs = [];
        this.currentIndex = null;
        this.docSourcesUpdated.emit(this.docSources);
    }
}
